import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

import { useIsDesktop } from "@/hooks/use-media-query";

/**
 * In-game, phones: should the open widget leave the game frame and cover the whole viewport?
 *
 * The in-game layer is positioned against the provider's game-frame element. On desktop that is
 * right — the game is a box on a page. On a phone the frame is often shorter than the screen
 * (provider chrome above, page below) and a panel bounded by it ends mid-screen with the campaign
 * sheet floating at the frame's edge. Below desktop width the layer goes `fixed` to the viewport
 * instead (never more than the iframe the casino opened, which on phones is the screen).
 */
export const useEscapesGameFrame = (active: boolean): boolean => {
  // Desktop keeps the panel inside the game frame; below `lg` it takes the viewport.
  const desktop = useIsDesktop();
  return active && !desktop;
};

type Parked = {
  host: HTMLElement;
  parent: Node;
  next: Node | null;
  cssText: string;
};

/**
 * While the layer covers the viewport, the widget's shadow HOST is lifted out of the game frame
 * onto `<body>`, and put back once the close animation has finished.
 *
 * `position: fixed` alone is not enough: the host sits inside the provider's frame wrapper, and
 * an ancestor with `overflow: hidden` (iOS Safari clips fixed descendants there), a transform or
 * filter (which re-anchor `fixed` to themselves), or its own stacking context (which caps our
 * z-index under the rest of the page) all cut the layer short — the Reels console's own
 * `overflow: hidden` host did exactly that on an iPhone. Under `<body>` nothing can. Moving the
 * node keeps the shadow root, the React tree and its state intact; only the launcher button, a
 * separate element, stays in the frame.
 *
 * Returns `ready` — gate the layer's mount on it — and `restore`, for the caller to run when the
 * exit animation completes (restoring on close would snap the still-animating layer back into the
 * clipped frame).
 *
 * The move happens in a LAYOUT effect, before paint, and the layer only mounts once it is done:
 * reparenting a node whose Motion animations are already running cancels their Web Animations
 * (and paints a frame of the layer inside the clipped frame first) — the open flicker.
 */
export const useHoistHostToBody = (
  anchorRef: RefObject<HTMLElement | null>,
  active: boolean
): { ready: boolean; restore: () => void } => {
  const parkedRef = useRef<Parked | null>(null);
  const [onBody, setOnBody] = useState(false);
  // Read by `restore`, which the caller runs from an exit-complete callback: a close followed by
  // a quick reopen fires that callback for the OLD layer while the new one is already open, and
  // restoring then would drop the open layer back into the clipped frame.
  const activeRef = useRef(active);
  activeRef.current = active;

  const restore = () => {
    const parked = parkedRef.current;
    if (!parked || activeRef.current) return;
    parkedRef.current = null;
    setOnBody(false);
    parked.host.style.cssText = parked.cssText;
    if (parked.parent.isConnected) {
      parked.parent.insertBefore(
        parked.host,
        parked.next?.parentNode === parked.parent ? parked.next : null
      );
    }
  };

  useLayoutEffect(() => {
    if (!active) return;
    const root = anchorRef.current?.getRootNode();
    const host = root instanceof ShadowRoot ? (root.host as HTMLElement) : null;
    if (!host || host.parentElement === document.body || parkedRef.current) {
      // Already on body (a reopen mid-close), or nothing to move (no shadow root): ready as is.
      setOnBody(true);
      return;
    }
    parkedRef.current = {
      host,
      parent: host.parentNode as Node,
      next: host.nextSibling,
      cssText: host.style.cssText,
    };
    document.body.appendChild(host);
    // The frame-bound host was `absolute; width/height 100%` of the frame: on body it is the
    // viewport itself. z-index and isolation stay as created.
    host.style.position = "fixed";
    host.style.top = "0";
    host.style.left = "0";
    host.style.width = "100%";
    host.style.height = "100%";
    // setState in a layout effect re-renders synchronously before paint: the layer's first
    // frame is already on body.
    setOnBody(true);
  }, [anchorRef, active]);

  // Unmount: never leave the host stranded on body.
  useEffect(
    () => () => {
      activeRef.current = false;
      restore();
    },
    []
  );

  return { ready: !active || onBody, restore };
};
